// The side column of widgets beside a content calendar, a pipeline or a sheet (Round 7, 21 Oct). It
// shows only while the board has widgets, so an empty one leaves the board its whole width, and the user can tuck it
// away to a tab on the right and open it again. That is kept on the board, so every device shows the same, and a
// widget added after it was tucked away (from the board's menu, the gallery or by an agent) brings the column back.
// The chat docks where the column is: while it is open, the same tab says the widgets wait behind it.

import { useRef, useState, useSyncExternalStore } from "react";
import { LayoutGrid, PanelRightClose } from "lucide-react";
import { SPEC } from "../../shared/catalog";
import type { Board, WidgetItem } from "../../shared/types";
import { closeChat, useChat } from "../agent/store";
import { cx } from "../lib/util";
import { commit, updateItem } from "../state/actions";
import { get } from "../state/store";

export { shelfState } from "../../shared/shelf";

/** Tucks the column away, or opens it again, on every device. Not a step to undo: it moves nothing on the board. */
export function tuckShelf(boardId: string, tucked: boolean) {
  const b = get().boards.find((x) => x.id === boardId);
  if (!b) return;
  commit([{ op: "board.put", board: { ...b, shelfHidden: tucked ? Date.now() : null } }], tucked ? "Hide the widgets" : "Show the widgets", { history: false });
}

/**
 * Whether the column's tab shows: while the column is tucked away, and while the chat covers it (Fixes 8, David: the
 * button that brings hidden widgets back "on the right, where the widgets are", always on screen and easy to see).
 */
export function useShelfEdge(side: "none" | "shown" | "tucked" | null): boolean {
  const chat = useChat((s) => s.open);
  return side === "tucked" || (side === "shown" && chat);
}

const NARROW = "(max-width: 900px)";
const narrowNow = () => typeof window !== "undefined" && !!window.matchMedia?.(NARROW).matches;
const onNarrow = (cb: () => void) => {
  const m = window.matchMedia?.(NARROW);
  m?.addEventListener("change", cb);
  return () => m?.removeEventListener("change", cb);
};

/**
 * Under about 900 wide the column starts folded on this screen alone, so a pipeline's four stages fit (Fixes 12); its
 * tab brings it back for as long as the board stays open. How the column stands here, and what the tab calls to unfold it.
 */
export function useFoldWhenNarrow(boardId: string, side: "none" | "shown" | "tucked"): { side: "none" | "shown" | "tucked"; unfold: () => void } {
  const narrow = useSyncExternalStore(onNarrow, narrowNow, () => false);
  const [unfolded, setUnfolded] = useState<string | null>(null);
  return { side: narrow && side === "shown" && unfolded !== boardId ? "tucked" : side, unfold: () => setUnfolded(boardId) };
}

/**
 * The column out of sight: a tab on the right where it was, beside the chat while the chat is open, with how many
 * widgets wait in it and their name. A click brings them back, and the chat steps aside for them.
 */
export function ShelfEdge({ board, count, className, onOpen }: { board: Board; count: number; className?: string; onOpen?: () => void }) {
  const chat = useChat((s) => s.open);
  return (
    <button
      className={cx("shelf-edge", className)}
      onClick={() => {
        if (board.shelfHidden) tuckShelf(board.id, false);
        if (chat) closeChat();
        onOpen?.();
      }}
      aria-label={`Show the widgets (${count})`}
      data-tip={chat ? "Show the widgets. The chat steps aside" : "Show the widgets"}
    >
      <LayoutGrid size={14} aria-hidden />
      <span className="shelf-edge-count">{count}</span>
      <span className="shelf-edge-name">Widgets</span>
    </button>
  );
}

/** Beside Add a widget at the column's foot: tucks the column away to its slim edge. */
export function ShelfHide({ board }: { board: Board }) {
  return (
    <button className="shelf-hide" onClick={() => tuckShelf(board.id, true)} aria-label="Hide the widgets" data-tip="Hide the widgets">
      <PanelRightClose size={15} />
    </button>
  );
}

/**
 * The foot of a widget tile that is dragged to change its height, in steps of 8 between min and max: the height being
 * dragged (null at rest, for the tile to draw) and the handle, given its own class and the tile's height now.
 */
export function useHeightGrip(item: WidgetItem | null | undefined, min: number, max: number) {
  const [drag, setDrag] = useState<number | null>(null);
  const start = useRef<{ y: number; h: number } | null>(null);
  const grip = (className: string, height: number) =>
    item && (
      <div
        className={className}
        role="separator"
        aria-orientation="horizontal"
        aria-label={`Drag to change the height of “${item.title || SPEC[item.type]?.label}”`}
        onPointerDown={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          start.current = { y: e.clientY, h: height };
          setDrag(height);
        }}
        onPointerMove={(e) => {
          if (!start.current) return;
          setDrag(Math.max(min, Math.min(max, Math.round((start.current.h + e.clientY - start.current.y) / 8) * 8)));
        }}
        onPointerUp={() => {
          const h = drag;
          start.current = null;
          setDrag(null);
          if (h !== null && h !== item.h) updateItem(item.id, { h }, "Resize widget");
        }}
        onPointerCancel={() => {
          start.current = null;
          setDrag(null);
        }}
      />
    );
  return { drag, grip };
}
